import React from "react";
import { Sparkles } from "lucide-react";

import { Card } from "@/components/ui/Card";
import { CHART } from "@/pages/admin/compliance/analytics/components/primitives";
import UsageSectionHeader from "./UsageSectionHeader";
import UsageStat from "./UsageStat";
import { fmt } from "./format";

/**
 * Token totals, plus the model-call detail when a SuperAdmin asked for it. The
 * second card of the usage report — see `UsageSummary.jsx`.
 *
 * `cachedCalls` and `failedCalls` sit *beside* the call count, never folded in:
 * a cache hit cost nothing and a failed call produced nothing, so neither
 * belongs in the figure a reader takes as "what we consumed" — but both are
 * needed to interpret it.
 */
const TokensCard = ({ tokens = {}, callDetail, features = [] }) => {
  const guardianCalls = features.filter((f) => f.isGuardian).reduce((sum, f) => sum + Number(f.calls ?? 0), 0);

  return (
    <Card className="p-5">
      <UsageSectionHeader
        icon={Sparkles}
        title={callDetail ? "Model calls and tokens" : "Tokens"}
        subtitle="Tokens consumed by the tools above. Work served entirely from cache consumed none."
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <UsageStat label="Input tokens" value={tokens.inputTokens} accent={CHART.blue} />
        <UsageStat label="Output tokens" value={tokens.outputTokens} accent={CHART.sky} />
        <UsageStat label="Total tokens" value={tokens.totalTokens} accent={CHART.indigo} />
      </div>

      {callDetail && (
        <div className="mt-5">
          <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-text-secondary">Model calls</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <UsageStat
              label="LLM calls"
              value={callDetail.calls}
              accent={CHART.violet}
              hint={Number(callDetail.cachedCalls ?? 0) > 0 ? `${fmt(callDetail.cachedCalls)} more served from cache (no model call)` : "None served from cache"}
            />
            <UsageStat label="Failed calls" value={callDetail.failedCalls} accent={CHART.red} hint="Not counted toward usage" />
            <UsageStat label="Prompt-safety calls" value={guardianCalls} accent={CHART.slate} hint="Included in the total above" />
          </div>
        </div>
      )}
    </Card>
  );
};

export default TokensCard;
